import { Component } from 'react';

import dynamic from 'next/dynamic';
import { getProPricingMenus } from 'modules/users/service';

import ProBreadcrumbs from '../ProBreadcrumbs';
import ProProfile from '../ProProfile';
import { ProProfilePro, PricingMenu } from '../../types';
import type { BlogPost } from 'modules/marketing-pages/marketing-page.types';
import {
  trackingEventForGA4,
  trackingEventForRequestCreation,
  GA4EventRequestCreation,
} from 'utils/eventTrackingForGA4';
import Image from 'next/image';
import styles from './ProProfileContent.module.scss';

const JobTypeSelectModal = dynamic(
  () => import(/* webpackChunkName: "job-type-select-modal" */ '../JobTypeSelectModal')
);

interface Props {
  featureFlags: { isReviewsQAEnabled: boolean };
  internalBlogPosts: Array<BlogPost>;
  internalBlogCostPosts: Array<BlogPost>;
  jobTypeId?: string;
  loadRelated: ({
    jobTypeId,
    requestableOnly,
  }: {
    jobTypeId: string;
    requestableOnly: boolean;
  }) => void;
  openRequestModal: (jobTypeId: string) => void;
  pro: ProProfilePro;
  requestableJobTypes: Array<{
    categories?: Array<string>;
    id: string;
    title: string;
  }>;
  setJobTypeTitle: (jobTypeId: string) => void;
  slugFromQueryParam: string;
}

interface State {
  isOpen: boolean;
  pricingMenus: Array<PricingMenu>;
}

class ProProfileContent extends Component<Props, State> {
  static defaultProps = {
    pro: undefined,
    requestableJobTypes: [],
    validItp: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      isOpen: false,
      pricingMenus: [],
    };
  }

  componentDidMount() {
    const { loadRelated, pro } = this.props;
    if (pro.enabledJobTypes && pro.enabledJobTypes.length === 1) {
      loadRelated({
        jobTypeId: pro.enabledJobTypes[0].id,
        requestableOnly: true,
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    getProPricingMenus({ userId: pro.id }).then(
      (
        pricingMenusByJobTypes: Array<{
          items: Array<{
            description: string;
            isMinimun?: boolean;
            key: string;
            price?: number;
            priceRange?: { min?: number; max?: number };
          }>;
          jobTypeId: string;
          title: string;
        }>
      ) => {
        const pricingMenus = pricingMenusByJobTypes.flatMap((pricingMenusByJobType) => ({
          jobTypeId: pricingMenusByJobType.jobTypeId,
          pricingMenus: pricingMenusByJobType.items.map((item) => ({
            ...item,
            title: pricingMenusByJobType.title,
          })),
        }));

        this.setState({ pricingMenus });
      }
    );

    if (pro.isItp) {
      trackingEventForGA4('proProfileItpViewed', 'proItp');
    }
  }

  onJobTypeSelected(jobTypeId: string) {
    const { openRequestModal, requestableJobTypes } = this.props;
    this.setState({
      isOpen: false,
    });
    const jobType = requestableJobTypes.find(
      (requestableJobType) => requestableJobType.id === jobTypeId
    );

    trackingEventForRequestCreation(GA4EventRequestCreation.startNewRequestClicked, {
      // eslint-disable-next-line id-length
      C1: jobType?.categories?.[0],
      // eslint-disable-next-line id-length
      C2: jobType?.categories?.[1],
      jobTypeId,
      whereFromForOpenRequestForm: 'afterProProfile',
    });

    openRequestModal(jobTypeId);
  }

  onRequestClicked() {
    const { jobTypeId, requestableJobTypes } = this.props;
    this.setState({
      isOpen: true,
    });
    const jobType = requestableJobTypes.find(
      (requestableJobType) => requestableJobType.id === jobTypeId
    );

    trackingEventForRequestCreation(GA4EventRequestCreation.findYourNeighborProsClicked, {
      // eslint-disable-next-line id-length
      C1: jobType?.categories?.[0],
      // eslint-disable-next-line id-length
      C2: jobType?.categories?.[1],
      jobTypeId,
      whereFromForOpenRequestForm: 'proProfile',
    });
  }

  render() {
    const {
      pro,
      featureFlags,
      internalBlogPosts,
      internalBlogCostPosts,
      jobTypeId,
      requestableJobTypes,
      slugFromQueryParam,
      setJobTypeTitle,
    } = this.props;
    const { isOpen, pricingMenus } = this.state;
    const defaultJobTypes = requestableJobTypes.filter((jobType) => jobType.id === jobTypeId);
    const defaultJobTypeId = defaultJobTypes.length ? defaultJobTypes[0].id : undefined;
    const preferredJobTypeWithUserSelection = [
      ...defaultJobTypes,
      ...requestableJobTypes.filter((jobType) => jobType.id !== jobTypeId),
    ];

    const jobTypes = pro.jobProfiles.map((jobProfile) => ({
      categories: jobProfile.categories,
      categoryTranslation: jobProfile.categoryTranslation,
      id: jobProfile.jobTypeId,
      title: jobProfile.label,
    }));

    const mainJobType =
      jobTypes.find((jobType) => jobType.id === (jobTypeId ?? pro.proProfile.mainJobType)) ??
      jobTypes[0];

    return (
      <>
        <div className={styles.wrapper}>
          <div className={styles.breadcrumb}>
            <ProBreadcrumbs jobType={mainJobType} pro={pro} />
          </div>
          <ProProfile
            internalBlogPosts={internalBlogPosts}
            internalBlogCostPosts={internalBlogCostPosts}
            featureFlags={featureFlags}
            jobTypeId={jobTypeId}
            pro={{ ...pro, pricingMenus, slug: slugFromQueryParam }}
            setJobTypeTitle={setJobTypeTitle}
          />
        </div>
        <div
          className={styles['cta-container']}
          onClick={() => this.onRequestClicked()}
          role="button"
          tabIndex={0}
        >
          <Image
            className={styles['cta-image']}
            src="/static/images/pro-profile-cta.svg"
            alt=""
            width="190"
            height="80"
          />
        </div>

        {isOpen && (
          <JobTypeSelectModal
            // eslint-disable-next-line react/jsx-no-bind
            dismiss={() => this.setState({ isOpen: false })}
            initialValues={{ jobTypeId: defaultJobTypeId }}
            jobTypes={preferredJobTypeWithUserSelection}
            // eslint-disable-next-line react/jsx-no-bind
            onSubmit={({ jobTypeId: selectedJobTypeId }: { jobTypeId: string }) =>
              this.onJobTypeSelected(selectedJobTypeId)
            }
            proName={pro.name}
          />
        )}
      </>
    );
  }
}

export default ProProfileContent;
